import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { intusBaseUrl } from '../utils/intusRedirect';

/**
 * SSO profesional desde INTUS: el profesional/admin/aliado entra con
 * ?professional_token=<sso> en el iframe embebido del dashboard de Vita360.
 * Intercambia el token por la sesión propia (POST /auth/sso/professional),
 * limpia la URL (token de un solo uso y sensible) y redirige a su dashboard.
 *
 * El token es de UN SOLO USO. React StrictMode (dev) monta/desmonta el
 * componente dos veces; `processedTokenRef` evita un segundo intercambio
 * con el mismo token.
 */
export default function ProfessionalSsoHandler() {
  const { setAuth, setLoading } = useAuthStore();
  const location = useLocation();
  const processedTokenRef = useRef<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('professional_token');

    if (!token) {
      return;
    }

    // Evitar doble intercambio del mismo token (StrictMode en dev).
    if (processedTokenRef.current === token) {
      return;
    }
    processedTokenRef.current = token;

    setLoading(true);
    setError(false);

    authService
      .ssoProfessionalLogin(token)
      .then((res) => {
        // Registrar la sesión en el store persistido ANTES de navegar.
        setAuth(res.usuario as never, res.accessToken, res.refreshToken, res.intusBaseUrl);

        // Navegación de página completa (no navigate() del router) para
        // evitar la race condition con IntusRedirect: montado en "/", lee
        // window.location.search y lanza redirectToIntus() si ve la URL sin
        // token y sin isLoading. Una navegación completa descarta IntusRedirect
        // del árbol (estábamos en "/") de forma atómica. La sesión ya quedó
        // persistida en localStorage, así que al cargar /dashboard está OK.
        window.location.replace(`${window.location.origin}/dashboard`);
      })
      .catch(() => {
        // Token rechazado: NO redirigir al flujo de paciente. Mostramos un
        // aviso neutral con enlace de vuelta al dashboard de Vita360.
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [location.search, setAuth, setLoading]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-v-on-surface-variant">
          No se pudo iniciar tu sesión en Vita360 App. Intenta nuevamente desde el portal de Vita360.
        </p>
        <a
          href={intusBaseUrl()}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-105"
        >
          Volver al portal de Vita360
        </a>
      </div>
    );
  }

  return null;
}
